import React, { useState, useRef, useEffect } from "react";
import { Form, ListGroup, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { searchUsers } from "../../../../redux/slices/communitySlice";
import "./MentionInput.scss";

/**
 * MentionInput Component
 * A text input that supports @mentions with user search
 *
 * @param {string} value - Current input value
 * @param {function} onChange - Callback when value changes
 * @param {function} onKeyPress - Callback for key press events
 * @param {string} placeholder - Input placeholder text
 * @param {boolean} autoFocus - Whether to autofocus the input
 * @param {object} style - Additional styles for the input
 * @param {string} className - Additional CSS classes
 */
const MentionInput = ({
  value,
  onChange,
  onKeyPress,
  placeholder = "Write a comment...",
  autoFocus = false,
  style = {},
  className = "",
}) => {
  const dispatch = useDispatch();
  const { searchUsersItems, isLoading } = useSelector(
    (state) => state.community,
  );

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [cursorPosition, setCursorPosition] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [currentLimit, setCurrentLimit] = useState(5);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [previousDataLength, setPreviousDataLength] = useState(0);

  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Extract users array from API response - data is directly the array
  const users = Array.isArray(searchUsersItems)
    ? searchUsersItems
    : searchUsersItems?.data || [];

  /**
   * Update previousDataLength when new data comes
   */
  useEffect(() => {
    if (users && users.length > 0) {
      setPreviousDataLength(users.length);
      setIsLoadingMore(false);
    }
  }, [users]);

  /**
   * Handle scroll to load more users
   */
  const handleScroll = (e) => {
    const container = e.target;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    // Check if scrolled to bottom (with 10px threshold)
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      // Check if we have more data to load (if current data length equals current limit, there might be more)
      const hasMore = users.length >= currentLimit;

      if (!isLoadingMore && hasMore && !isLoading) {
        setIsLoadingMore(true);
        const newLimit = currentLimit + 5; // Increase limit by 5
        setCurrentLimit(newLimit);

        // Load more users with increased limit
        dispatch(searchUsers({ query: mentionQuery, limit: newLimit }));
      }
    }
  };

  /**
   * Detect @ mentions in the text
   * Excludes @ symbols that are part of email addresses
   */
  const detectMention = (text, position) => {
    const textBeforeCursor = text.substring(0, position);
    const match = textBeforeCursor.match(/@(\w*)$/);

    if (match) {
      // Check if @ is part of an email address (preceded by word characters or dots)
      const charBeforeAt =
        textBeforeCursor[textBeforeCursor.length - match[0].length - 1];

      // If there's a character before @ that looks like part of an email, skip mention detection
      if (charBeforeAt && /[\w.]/.test(charBeforeAt)) {
        return null;
      }

      return {
        query: match[1],
        startPos: position - match[1].length - 1,
      };
    }
    return null;
  };

  /**
   * Auto-resize textarea based on content
   */
  const autoResizeTextarea = (textarea) => {
    if (!textarea) return;
    
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';
    
    // Set height based on scrollHeight, but respect min/max constraints
    const minHeight = 42;
    const maxHeight = 120;
    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
    
    textarea.style.height = `${newHeight}px`;
  };

  /**
   * Handle input change
   */
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const newCursorPos = e.target.selectionStart;

    // Auto-resize textarea
    autoResizeTextarea(e.target);

    onChange(e);
    setCursorPosition(newCursorPos);

    // Check for @ mention
    const mention = detectMention(newValue, newCursorPos);

    if (mention) {
      setMentionQuery(mention.query);
      setShowSuggestions(true);
      setSelectedIndex(0);
      setCurrentLimit(5); // Reset to initial limit
      setPreviousDataLength(0); // Reset previous data length

      // Debounce search - 2 seconds
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        if (mention.query.length >= 0) {
          dispatch(searchUsers({ query: mention.query, limit: 5 }));
        }
      }, 1000); // 2 seconds debounce
    } else {
      setShowSuggestions(false);
      setMentionQuery("");
      setCurrentLimit(5);
      setPreviousDataLength(0);
    }
  };

  /**
   * Handle user selection from suggestions
   */
  const selectUser = (user) => {
    const mention = detectMention(value, cursorPosition);
    if (!mention) return;

    const beforeMention = value.substring(0, mention.startPos);
    const afterMention = value.substring(cursorPosition);
    // Insert the user's full name (fall back to username if it's empty).
    // Add double space after mention to properly delimit it for parsing.
    const mentionName = user.full_name?.trim() || user.username;
    const newValue = `${beforeMention}@${mentionName}  ${afterMention}`;

    // Create synthetic event for onChange
    const syntheticEvent = {
      target: {
        value: newValue,
      },
    };
    onChange(syntheticEvent);

    setShowSuggestions(false);
    setMentionQuery("");

    // Focus back on input
    setTimeout(() => {
      if (inputRef.current) {
        const newCursorPos = mention.startPos + mentionName.length + 3; // +3 for @ and two spaces
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  /**
   * Handle keyboard navigation in suggestions
   */
  const handleKeyDown = (e) => {
    if (!showSuggestions || users.length === 0) {
      if (onKeyPress) {
        onKeyPress(e);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % users.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + users.length) % users.length);
        break;
      case "Enter":
        if (showSuggestions && users[selectedIndex]) {
          e.preventDefault();
          selectUser(users[selectedIndex]);
        } else if (onKeyPress) {
          onKeyPress(e);
        }
        break;
      case "Escape":
        e.preventDefault();
        setShowSuggestions(false);
        break;
      default:
        if (onKeyPress) {
          onKeyPress(e);
        }
        break;
    }
  };

  /**
   * Close suggestions when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /**
   * Auto-resize textarea when value changes externally
   */
  useEffect(() => {
    if (inputRef.current) {
      autoResizeTextarea(inputRef.current);
    }
  }, [value]);

  /**
   * Cleanup timeout on unmount
   */
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="mention-input-wrapper" style={{ position: "relative" }}>
      <Form.Control
        as="textarea"
        ref={inputRef}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoFocus={autoFocus}
        style={{
          ...style,
          resize: "none",
          minHeight: "42px",
          maxHeight: "120px",
          overflowY: "auto",
          wordWrap: "break-word",
          whiteSpace: "pre-wrap"
        }}
        className={className}
        rows={1}
      />

      {/* Suggestions Dropdown */}
      {showSuggestions && (
        <div
          ref={suggestionsRef}
          className="mention-suggestions"
          style={{
            position: "absolute",
            bottom: "100%",
            left: 0,
            right: 0,
            marginBottom: "4px",
            maxHeight: "200px",
            overflowY: "auto",
            borderRadius: "8px",
            boxShadow: "0 4px 6px var(--mmp-shadow)",
            zIndex: 1000,
          }}
          onScroll={handleScroll}
        >
          {isLoading && currentLimit === 5 ? (
            <div className="text-center p-3 mention-status">
              <Spinner animation="border" size="sm" />
              <span className="ms-2">Searching users...</span>
            </div>
          ) : users.length > 0 ? (
            <>
              <ListGroup variant="flush">
                {users.map((user, index) => (
                  <ListGroup.Item
                    key={`${user.id}-${index}`}
                    action
                    active={index === selectedIndex}
                    onClick={() => selectUser(user)}
                    style={{
                      cursor: "pointer",
                      padding: "8px 12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <img
                      src={
                        user.profile_photo_path
                          ? `${process.env.REACT_APP_API_BASE_URL}/uploads/user_profile/${user.profile_photo_path}`
                          : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              user.full_name || "User",
                            )}&background=0195D3&color=fff&size=32`
                      }
                      alt={user.full_name}
                      style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          user.full_name || "User",
                        )}&background=0195D3&color=fff&size=32`;
                      }}
                    />
                    <div>
                      <div style={{ fontWeight: 500, fontSize: "14px" }}>
                        {user.full_name?.trim() || user.username}
                      </div>
                      <div className="mention-username" style={{ fontSize: "12px" }}>
                        @{user.username}
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>

              {/* Loading more indicator */}
              {isLoadingMore && (
                <div
                  className="text-center p-2 mention-status"
                  style={{ borderTop: "1px solid var(--mmp-border)" }}
                >
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2" style={{ fontSize: "12px" }}>
                    Loading more...
                  </span>
                </div>
              )}

              {/* End of results indicator */}
              {!isLoadingMore &&
                users.length < currentLimit &&
                users.length > 0 && (
                  <div
                    className="text-center p-2 mention-status"
                    style={{ borderTop: "1px solid var(--mmp-border)" }}
                  >
                    <small>No more users</small>
                  </div>
                )}
            </>
          ) : (
            <div className="text-center p-3 mention-status">
              <small>No users found</small>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MentionInput;
